import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/system';
import { CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { AgentChat, AgentChatWithMessages } from 'bloggers-backend/lib/aiagents/domain';
import { AppContext } from '../../AppContext';
import AiAgentEditForm from './components/AiAgentEditForm';
import ChatSidebar from './components/ChatSidebar';
import ChatConversation from './components/ChatConversation';
import InvokeAgentBox from './components/InvokeAgentBox';

// Newest chats first; createdAt is an ISO string so a Mongo sort on it is chronological. A large
// page size keeps every chat in the sidebar without paging.
const SORT_MODEL = [{ field: 'createdAt', sort: 'desc' }];
const PAGE_SIZE = 1000;

// Poll while a reply is still being generated by the worker so it appears without a manual refresh.
const POLL_INTERVAL_MS = 2000;
const isPending = (chat: AgentChat) => chat.status === 'in progress';
const hasPendingMessage = (chats: AgentChat[]) => chats.some(isPending);

export default function EditAiAgentPage() {
  const { id } = useParams();
  const { client, notificationsService } = React.useContext(AppContext);

  // Scope the chats cache to this agent so navigating between agents doesn't
  // show another agent's cached rows; InvokeAgentBox invalidates the same key.
  const chatsCacheName = `agentChats-${id}`;

  // The chat currently shown; empty means the next message starts a new chat.
  const [currentChatId, setCurrentChatId] = React.useState('');

  // The message the user just sent, rendered optimistically until the worker checkpoints it and a
  // poll returns it in the open chat. Cleared by ChatConversation once the server catches up.
  const [pendingInput, setPendingInput] = React.useState<string | null>(null);

  const { data, isLoading } = useQuery<AgentChat[]>(
    [chatsCacheName],
    async () => {
      const result = await client<any, any>({
        event: 'aiagents.getChats',
        data: { agentId: id, sortModel: SORT_MODEL, pageSize: PAGE_SIZE },
      });

      if (result.success) {
        return result.value.data as AgentChat[];
      }

      notificationsService.notify({
        severity: 'error',
        message: `Error: ${JSON.stringify(result.errors)}`,
      });

      return [];
    },
    { refetchInterval: (chats) => (chats && hasPendingMessage(chats) ? POLL_INTERVAL_MS : false) },
  );

  // The chats list holds only metadata; the open chat's messages come from the checkpoint via
  // getChat. Poll it while its turn is in progress so the reply appears without a manual refresh.
  const { data: openChat } = useQuery<AgentChatWithMessages | null>(
    [chatsCacheName, 'chat', currentChatId],
    async () => {
      const result = await client<any, any>({
        event: 'aiagents.getChat',
        data: { id: currentChatId },
      });

      if (result.success) {
        return result.value as AgentChatWithMessages | null;
      }

      notificationsService.notify({
        severity: 'error',
        message: `Error: ${JSON.stringify(result.errors)}`,
      });

      return null;
    },
    {
      enabled: !!currentChatId,
      refetchInterval: (chat) => (chat && isPending(chat) ? POLL_INTERVAL_MS : false),
    },
  );

  const chats = data ?? [];

  if (!id) {
    return <></>;
  }

  return (
    <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'row', minHeight: 0, overflow: 'hidden' }}>
      <ChatSidebar
        chats={chats}
        currentChatId={currentChatId}
        onSelectChat={(chatId) => {
          setCurrentChatId(chatId);
          setPendingInput(null);
        }}
        onNewChat={() => {
          setCurrentChatId('');
          setPendingInput(null);
        }}
      />

      <Box sx={{ flexGrow: 1, height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AiAgentEditForm />

        <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <ChatConversation
              chat={currentChatId ? openChat ?? undefined : undefined}
              chatsCacheName={chatsCacheName}
              pendingInput={pendingInput}
              onPendingConsumed={() => setPendingInput(null)}
            />
          )}
        </Box>

        <InvokeAgentBox
          agentId={id}
          chatsCacheName={chatsCacheName}
          currentChatId={currentChatId}
          onMessageSent={(chatId, input) => {
            setCurrentChatId(chatId);
            setPendingInput(input);
          }}
        />
      </Box>
    </Box>
  );
}
